import { BEIGE_300, BEIGE_600, GRAY_400, GRAY_700, GRAY_900, WHITE } from 'constants/styling/theme'
import { Box, Stack, Typography } from '@mui/material'

import { AnalyticsEvent } from 'utils/analytics'
import BorderAllOutlinedIcon from '@mui/icons-material/BorderAllOutlined'
import ChairOutlinedIcon from '@mui/icons-material/ChairOutlined'
import CleaningServicesOutlinedIcon from '@mui/icons-material/CleaningServicesOutlined'
import { EditionType } from '../../../photoStudioTypes'
import { FC } from 'react'
import FormatPaintOutlinedIcon from '@mui/icons-material/FormatPaintOutlined'
import LockOutlinedIcon from '@mui/icons-material/LockOutlined'
import { MUIBadge } from 'components/common/MUIBadge'
import { MUIButton } from 'components/common/MUIButton'
import SyncOutlinedIcon from '@mui/icons-material/SyncOutlined'
import { externalNavigate } from 'utils/helpers'
import { getPricingUrl } from 'utils/localization'
import i18n from 'translations/i18n'
import { useClientPhotoStudio } from '../../../ClientPhotoStudio.context'
import { useTranslation } from 'react-i18next'

/**
 * Upsell component for non-subscribers in Photo Studio Virtual Staging Panel
 * Displays a promotional section encouraging users to subscribe to access Virtual Staging feature
 * 
 * @example <NonSubscriberVirtualStagingUpsell />
 */
export const NonSubscriberVirtualStagingUpsell: FC = () => {
  const { t } = useTranslation(['photo_studio_editing', 'common'])
  const { logPhotoStudioEvent } = useClientPhotoStudio()

  const handleSubscribeClick = () => {
    logPhotoStudioEvent(AnalyticsEvent.PHOTO_STUDIO_SAAS_UPSELL, { editionType: EditionType.VIRTUAL_STAGING })
    externalNavigate(getPricingUrl(i18n.language))
  }

  const roomOptions = [
    { icon: <ChairOutlinedIcon sx={{ fontSize: '1.6rem', color: GRAY_700 }} />, label: t('virtual_staging.room_options_unsubscribed.stage_empty_room') },
    { icon: <SyncOutlinedIcon sx={{ fontSize: '1.6rem', color: GRAY_700 }} />, label: t('virtual_staging.room_options_unsubscribed.restyle_room') },
    { icon: <CleaningServicesOutlinedIcon sx={{ fontSize: '1.6rem', color: GRAY_700 }} />, label: t('virtual_staging.room_options_unsubscribed.declutter_room') },
  ]

  const surfaceOptions = [
    { icon: <FormatPaintOutlinedIcon sx={{ fontSize: '1.6rem', color: GRAY_700 }} />, label: t('virtual_staging.surface_options_unsubscribed.paint_walls') },
    { icon: <BorderAllOutlinedIcon sx={{ fontSize: '1.6rem', color: GRAY_700 }} />, label: t('virtual_staging.surface_options_unsubscribed.change_floor') },
  ]

  return (
    <Box
      sx={{
        backgroundColor: BEIGE_600,
        borderRadius: '.8rem',
        height: 'fit-content',
        margin: '1.6rem',
        padding: '1.6rem',

      }}
    >
      <Stack gap={2.4}>
        <Stack gap={1.2}>
          <MUIBadge
            size="xs"
            color="coral"
            icon={<LockOutlinedIcon fontSize="small" />}
            label={t('common:Subscribers_only')}
            sx={{ width: 'fit-content' }}
          />

          <Stack gap={0.4}>
            <Typography variant="text-sm" fontWeight={600} color={GRAY_900}>
              {t('non_subscribers_title')}
            </Typography>
            <Typography variant="text-xs" fontWeight={400} color={GRAY_700}>
              {t('non_subscribers_description')}
            </Typography>
          </Stack>

          <MUIButton
            sx={{ marginTop: 1, fontSize: '12px', width: 'fit-content' }}
            size="sm"
            onClick={handleSubscribeClick}
          >
            {t('subscribe_to_bkbn_button')}
          </MUIButton>
        </Stack>

        {/* Room Options Section */}
        <Box
          sx={{
            backgroundColor: BEIGE_300,
            borderRadius: '.8rem',
            padding: '1.6rem',
            width: '100%',
          }}
        >
          <Stack gap={1.6}>
            {/* Furnitures Section */}
            <Stack gap={0.6}>
              <Typography
                variant="text-xs"
                fontWeight={450}
                color={GRAY_700}
                sx={{
                  fontSize: '1.2rem',
                  lineHeight: 1.5,
                  letterSpacing: '-0.48px',
                }}
              >
                {t('virtual_staging.room_options_unsubscribed.furnitures')}
              </Typography>
              <Stack gap={0.8}>
                {roomOptions.map((option, index) => (
                  <Box
                    key={index}
                    sx={{
                      backgroundColor: WHITE,
                      border: `1px solid ${GRAY_400}`,
                      borderRadius: '.8rem',
                      padding: '1.2rem',
                      display: 'flex',
                      flexDirection: 'row',
                      alignItems: 'center',
                      gap: '0.8rem',
                      cursor: 'not-allowed',
                      position: 'relative',
                    }}
                  >
                    {option.icon}
                    <Typography
                      variant="text-sm"
                      fontWeight={350}
                      color={GRAY_700}
                      sx={{
                        fontSize: '1.4rem',
                        lineHeight: 1.4,
                        letterSpacing: '-0.56px',
                        flex: 1,
                      }}
                    >
                      {option.label}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            </Stack>

            {/* Surfaces Section */}
            <Stack gap={0.6}>
              <Typography
                variant="text-xs"
                fontWeight={450}
                color={GRAY_700}
                sx={{
                  fontSize: '1.2rem',
                  lineHeight: 1.5,
                  letterSpacing: '-0.48px',
                }}
              >
                {t('virtual_staging.surface_options_unsubscribed.surfaces')}
              </Typography>
              <Stack gap={0.8}>
                {surfaceOptions.map((option, index) => (
                  <Box
                    key={index}
                    sx={{
                      backgroundColor: WHITE,
                      border: `1px solid ${GRAY_400}`,
                      borderRadius: '.8rem',
                      padding: '1.2rem',
                      display: 'flex',
                      flexDirection: 'row',
                      alignItems: 'center',
                      gap: '0.8rem',
                      cursor: 'not-allowed',
                      position: 'relative',
                    }}
                  >
                    {option.icon}
                    <Typography
                      variant="text-sm"
                      fontWeight={350}
                      color={GRAY_700}
                      sx={{
                        fontSize: '1.4rem',
                        lineHeight: 1.4,
                        letterSpacing: '-0.56px',
                        flex: 1,
                      }}
                    >
                      {option.label}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            </Stack>
          </Stack>
        </Box>
      </Stack>
    </Box>
  )
}
